import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  LayoutDashboard, GraduationCap, Award, Heart, HandHeart, ArrowRight,
  Sparkles, User as UserIcon, CheckCircle2, Loader2, IdCard, BookOpen, Users,
} from "lucide-react";

function initials(name = "") { return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "IC"; }

export default function Associate() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/me/enrollments").then(({ data }) => setEnrollments(data)).catch(() => {}),
      api.get("/me/certificates").then(({ data }) => setCertificates(data)).catch(() => {}),
      api.get("/me/donor").then(({ data }) => setDonor(data)).catch(() => setDonor({ has_subscription: false })),
    ]).finally(() => setLoading(false));
  }, [user?.id]);

  if (!user) return null;
  const photoUrl = user.profile_photo_path ? `${API}/files/${user.profile_photo_path}` : null;
  const s = user.supporter || null;

  const totalCompleted = enrollments.filter((e) => e.completed_at).length;
  const totalInProgress = enrollments.filter((e) => !e.completed_at).length;
  const certsUnlocked = certificates.filter((c) => c.paid).length;

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--primary))]/8 via-white to-[hsl(var(--secondary))]/8">
        <div className="container-cuidar py-10">
          <div className="flex items-center gap-4 flex-wrap">
            <Avatar className="h-16 w-16 border-4 border-white shadow-md">
              {photoUrl && <AvatarImage src={photoUrl} alt={user.name} />}
              <AvatarFallback className="bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-heading font-black">
                {initials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--secondary))] font-bold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Área do Associado
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black">Olá, {user.name.split(" ")[0]}!</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Aqui estão seus cursos, certificados, cadastro e mais.
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={GraduationCap} label="Cursos em andamento" value={totalInProgress} testid="kpi-inprogress" />
            <Kpi icon={CheckCircle2} label="Cursos concluídos" value={totalCompleted} testid="kpi-completed" />
            <Kpi icon={Award} label="Certificados desbloqueados" value={certsUnlocked} testid="kpi-certs" />
            <Kpi icon={Heart} label="Perfil de apoio" value={s ? "Ativo" : "Inativo"} testid="kpi-supporter" />
          </div>
        </div>
      </section>

      <section className="container-cuidar py-10">
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="bg-transparent gap-2 flex-wrap">
            <TabsTrigger value="dashboard" className="rounded-full px-4 py-2 gap-1.5" data-testid="assoc-tab-dashboard"><LayoutDashboard className="h-4 w-4" /> Visão geral</TabsTrigger>
            <TabsTrigger value="courses" className="rounded-full px-4 py-2 gap-1.5" data-testid="assoc-tab-courses"><BookOpen className="h-4 w-4" /> Meus cursos</TabsTrigger>
            <TabsTrigger value="certs" className="rounded-full px-4 py-2 gap-1.5" data-testid="assoc-tab-certs"><Award className="h-4 w-4" /> Certificados</TabsTrigger>
            <TabsTrigger value="account" className="rounded-full px-4 py-2 gap-1.5" data-testid="assoc-tab-account"><UserIcon className="h-4 w-4" /> Cadastro</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="pt-6">
            <DashboardOverview user={user} enrollments={enrollments} certs={certificates} supporter={s} donor={donor} />
          </TabsContent>
          <TabsContent value="courses" className="pt-6">
            <MyCourses enrollments={enrollments} loading={loading} />
          </TabsContent>
          <TabsContent value="certs" className="pt-6">
            <Certificates certificates={certificates} enrollments={enrollments} refresh={() => api.get("/me/certificates").then(({ data }) => setCertificates(data))} />
          </TabsContent>
          <TabsContent value="account" className="pt-6">
            <AccountSummary user={user} />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="surface p-5 flex items-center gap-4" data-testid={testid}>
      <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--primary))]/10 grid place-items-center text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">{label}</div>
        <div className="font-heading text-2xl font-black">{value}</div>
      </div>
    </div>
  );
}

function DashboardOverview({ user, enrollments, certs, supporter, donor }) {
  const inProgress = enrollments.filter((e) => !e.completed_at).slice(0, 3);
  const hasDonation = donor?.has_subscription;
  const openPortal = async () => {
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/donations/portal", { email: user.email, origin });
      if (data.url) {
        // Abre em nova aba (Apple Store: doações/portal fora do webview do app)
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) window.location.href = data.url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao abrir portal."); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <div className="surface p-6">
          <div className="flex items-center justify-between">
            <div className="font-heading font-black text-lg">Continue de onde parou</div>
            <Link to="/cursos" className="text-sm text-[hsl(var(--primary))] font-semibold hover:underline">Ver todos <ArrowRight className="h-3 w-3 inline" /></Link>
          </div>
          {inProgress.length === 0 ? (
            <div className="mt-6 text-sm text-[hsl(var(--muted-foreground))] text-center py-8">
              Você ainda não tem cursos em andamento. <Link to="/cursos" className="text-[hsl(var(--primary))] font-semibold hover:underline">Explorar cursos →</Link>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {inProgress.map((e) => (
                <Link key={e.course.id} to={`/cursos/${e.course.slug}`} className="flex items-center gap-4 rounded-2xl border border-[hsl(var(--border))] p-4 hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className="h-12 w-12 rounded-xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] grid place-items-center"><GraduationCap className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm">{e.course.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{e.modules_done}/{e.modules_total} módulos</div>
                    <Progress value={e.progress_percent} className="h-1.5 mt-2" />
                  </div>
                  <ArrowRight className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="surface p-6">
          <div className="font-heading font-black text-lg">Atalhos</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ShortcutCard icon={IdCard} to="/carteirinha" title="Minha carteirinha" desc="PDF em tamanho oficial CR80 pronto para imprimir." />
            <ShortcutCard icon={BookOpen} to="/direitos" title="Área de Direitos" desc="Guias sobre INSS, BPC, direitos escolares e mais." />
            <ShortcutCard icon={Users} to="/comunidade" title="Comunidade" desc="Postagens, desabafos e apoio das famílias." />
            <ShortcutCard icon={Heart} to="/doacoes" title="Doar" desc="Contribua para sustentar o instituto." />
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div className="surface p-6 bg-[hsl(var(--primary))]/6 border-[hsl(var(--primary))]/20">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))]">Certificados</div>
          <div className="font-heading font-black text-lg">{certs.length > 0 ? `${certs.filter((c) => c.paid).length} de ${certs.length}` : "Nenhum ainda"}</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Certificados desbloqueados para download.</p>
          <Link to="#" onClick={(e) => e.preventDefault()} className="mt-3 inline-flex items-center gap-1 text-sm text-[hsl(var(--primary))] font-semibold">
            Ver na aba Certificados <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className={`surface p-6 ${supporter ? "bg-[hsl(var(--secondary))]/8 border-[hsl(var(--secondary))]/30" : ""}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))]">Apoio</div>
          <div className="font-heading font-black text-lg">{supporter ? "Você é apoiador(a) ativo" : "Torne-se apoiador(a)"}</div>
          {supporter ? (
            <>
              <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 space-y-1">
                {supporter.is_volunteer && <div>Voluntário(a) em: {(supporter.volunteer_areas || []).join(", ") || "áreas não informadas"}</div>}
                {supporter.is_donor && <div>Doador(a){supporter.monthly_pledge ? ` — R$ ${supporter.monthly_pledge}/mês` : ""}</div>}
              </div>
              <Link to="/perfil" className="mt-3 inline-flex items-center gap-1 text-sm text-[hsl(var(--secondary))] font-semibold">
                Editar meu apoio <ArrowRight className="h-3 w-3" />
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Você é associado ao Instituto Cuidar. Amplie seu papel virando voluntário(a) ou doador(a).</p>
              <Link to="/apoiar" className="mt-3 inline-flex items-center gap-1 text-sm text-[hsl(var(--primary))] font-semibold">
                Ativar apoio <ArrowRight className="h-3 w-3" />
              </Link>
            </>
          )}
        </div>

        {hasDonation && (
          <div className="surface p-6 bg-[hsl(var(--primary))]/6 border-[hsl(var(--primary))]/25" data-testid="donor-card">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))] flex items-center gap-1"><Heart className="h-3 w-3" /> Sua doação mensal</div>
            <div className="mt-2 font-heading text-3xl font-black text-[hsl(var(--primary))]">R$ {Number(donor.monthly_amount || 0).toFixed(2)}<span className="text-sm font-medium text-[hsl(var(--muted-foreground))]"> / mês</span></div>
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 space-y-0.5">
              {donor.past_due && <div className="text-amber-700 font-semibold">Pagamento em atraso — atualize seu cartão.</div>}
              {donor.canceled_at && <div>Cancelada em {new Date(donor.canceled_at).toLocaleDateString("pt-BR")}</div>}
              {donor.last_payment_at && <div>Última cobrança: {new Date(donor.last_payment_at).toLocaleDateString("pt-BR")}</div>}
              {donor.created_at && <div>Ativa desde {new Date(donor.created_at).toLocaleDateString("pt-BR")}</div>}
            </div>
            <button onClick={openPortal} className="mt-3 inline-flex items-center gap-1 text-sm text-[hsl(var(--primary))] font-semibold hover:underline" data-testid="donor-portal-btn">
              Gerenciar / alterar cartão <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ShortcutCard({ icon: Icon, to, title, desc }) {
  return (
    <Link to={to} className="rounded-2xl border border-[hsl(var(--border))] p-4 hover:-translate-y-0.5 hover:shadow-md transition-all block">
      <div className="h-10 w-10 rounded-xl bg-[hsl(var(--muted))] grid place-items-center text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-heading font-bold text-sm">{title}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{desc}</div>
    </Link>
  );
}

function MyCourses({ enrollments, loading }) {
  if (loading) return <div className="surface p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">Carregando…</div>;
  if (enrollments.length === 0) {
    return (
      <div className="surface p-10 text-center">
        <GraduationCap className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
        <div className="mt-3 font-heading font-bold">Você ainda não se matriculou em nenhum curso</div>
        <Link to="/cursos" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:underline">Explorar cursos <ArrowRight className="h-4 w-4" /></Link>
      </div>
    );
  }
  return (
    <div className="grid gap-4">
      {enrollments.map((e) => (
        <div key={e.course.id} className="surface p-5 flex items-center gap-4 flex-wrap sm:flex-nowrap" data-testid={`enrollment-${e.course.slug}`}>
          <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary))]/70 grid place-items-center shrink-0"><GraduationCap className="h-6 w-6 text-white" /></div>
          <div className="flex-1 min-w-0">
            <Badge variant="outline" className="rounded-full text-[10px]">{e.course.category}</Badge>
            <div className="mt-1 font-heading font-extrabold">{e.course.title}</div>
            <div className="mt-2 flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
              <span>{e.modules_done}/{e.modules_total} módulos</span>
              <span>{e.progress_percent}%</span>
              {e.completed_at && <span className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Concluído</span>}
            </div>
            <Progress value={e.progress_percent} className="h-1.5 mt-2 max-w-md" />
          </div>
          <Link to={`/cursos/${e.course.slug}`} className="shrink-0">
            <Button className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
              {e.completed_at ? "Revisar" : "Continuar"} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      ))}
    </div>
  );
}

function Certificates({ certificates, enrollments, refresh }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [polling, setPolling] = useState(null);
  const [processing, setProcessing] = useState(null);

  // Handle Stripe return URL: ?session_id=...&code=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const session = params.get("session_id");
    const code = params.get("code");
    if (session && code) {
      let attempts = 0;
      setPolling(code);
      const poll = async () => {
        attempts++;
        try {
          const { data } = await api.get(`/checkout/status/${session}`);
          if (data.payment_status === "paid") {
            setPolling(null);
            toast.success("Pagamento confirmado! Certificado desbloqueado.");
            navigate("/associado/certificados", { replace: true });
            refresh?.();
            return;
          }
          if (data.status === "expired") {
            setPolling(null);
            toast.error("Sessão de pagamento expirada.");
            navigate("/associado/certificados", { replace: true });
            return;
          }
          if (attempts >= 8) {
            setPolling(null);
            toast.warning("Pagamento em processamento. Volte em alguns instantes.");
            return;
          }
          setTimeout(poll, 2000);
        } catch (_) {
          setPolling(null);
          toast.error("Falha ao verificar pagamento.");
        }
      };
      poll();
    }
    // eslint react-hooks/exhaustive-deps intentionally not depending on navigate/refresh
  }, [location.search]);

  const checkout = async (code) => {
    setProcessing(code);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/checkout/certificate/${code}`, { origin });
      if (data.already_paid) {
        toast.info("Este certificado já está desbloqueado.");
        refresh?.();
        setProcessing(null);
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar pagamento.");
      setProcessing(null);
    }
  };

  const download = (code) => {
    // authenticated blob download
    api.get(`/me/certificates/${code}.pdf`, { responseType: "blob" }).then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `certificado-${code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    }).catch(() => toast.error("Falha ao baixar."));
  };

  if (polling) {
    return (
      <div className="surface p-10 text-center" data-testid="cert-polling">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-[hsl(var(--primary))]" />
        <div className="mt-3 font-heading font-bold">Confirmando seu pagamento…</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Isso leva poucos segundos.</p>
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="surface p-10 text-center">
        <Award className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
        <div className="mt-3 font-heading font-bold">Nenhum certificado ainda</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Certificados são emitidos automaticamente ao concluir um curso com aproveitamento &gt;= 70%.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {certificates.map((c) => (
        <div key={c.code} className={`surface p-6 flex items-center gap-4 flex-wrap sm:flex-nowrap ${c.paid ? "border-[hsl(var(--primary))]/30" : ""}`} data-testid={`cert-${c.code}`}>
          <div className={`h-14 w-14 rounded-xl grid place-items-center shrink-0 ${c.paid ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"}`}>
            <Award className={`h-6 w-6 ${c.paid ? "text-white" : "text-[hsl(var(--muted-foreground))]"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-bold">Certificado</div>
            <div className="font-heading font-extrabold">{c.course_title}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Código {c.code} · Emitido {c.issued_at ? new Date(c.issued_at).toLocaleDateString("pt-BR") : ""}</div>
          </div>
          <div className="shrink-0">
            {c.paid ? (
              <Button onClick={() => download(c.code)} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid={`cert-download-${c.code}`}>
                Baixar PDF
              </Button>
            ) : (
              <Button onClick={() => checkout(c.code)} disabled={processing === c.code} className="rounded-full bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(var(--secondary))]/90" data-testid={`cert-pay-${c.code}`}>
                {processing === c.code ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Abrindo…</> : "Desbloquear · R$ 15"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountSummary({ user }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="surface p-6">
        <div className="font-heading font-black">Dados pessoais</div>
        <dl className="mt-4 grid gap-3 text-sm">
          <Row label="Nome" value={user.name} />
          <Row label="E-mail" value={user.email} />
          <Row label="WhatsApp" value={user.phone || "—"} />
          <Row label="Cidade / UF" value={[user.city, user.state].filter(Boolean).join(" / ") || "—"} />
        </dl>
        <Link to="/perfil"><Button variant="outline" className="rounded-full mt-4">Editar perfil <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
      </div>
      <div className="surface p-6">
        <div className="font-heading font-black">Da criança</div>
        <dl className="mt-4 grid gap-3 text-sm">
          <Row label="Nome" value={user.child_name || "—"} />
          <Row label="Situação" value={user.child_condition || "—"} />
          <Row label="Nascimento" value={user.child_birth_date || "—"} />
        </dl>
        <Link to="/carteirinha"><Button variant="outline" className="rounded-full mt-4">Ver carteirinha <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <dt className="text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
